import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Truck, RefreshCw, ExternalLink, MapPin, Phone, Clock, MessageCircle,
  StickyNote, PhoneCall, PhoneOff,
} from "lucide-react";
import { toast } from "sonner";
import {
  getShipment, syncOne, STATUS_LABELS, STATUS_COLORS, STATUS_DOT,
  getShipmentNotes, addShipmentNote, toggleCallFlag,
} from "@/lib/delivery";
import { fmtDateTz } from "@/lib/format";
import { formatApiError } from "@/lib/api";

export default function ShipmentDetailDrawer({ awb, onClose, onUpdate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [notes, setNotes] = useState([]);
  const [callFlag, setCallFlag] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);

  useEffect(() => {
    if (!awb) {
      setData(null);
      setNotes([]);
      setCallFlag(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getShipment(awb).catch((e) => { toast.error(formatApiError(e)); return null; }),
      getShipmentNotes(awb).catch(() => ({ notes: [], flag_to_call: false })),
    ])
      .then(([ship, meta]) => {
        setData(ship);
        setNotes(meta?.notes || []);
        setCallFlag(!!meta?.flag_to_call);
      })
      .finally(() => setLoading(false));
  }, [awb]);

  const onResync = async () => {
    setSyncing(true);
    try {
      await syncOne(awb);
      const r = await getShipment(awb);
      setData(r);
      toast.success("Status actualizat din MyGLS");
      onUpdate?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncing(false);
    }
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setNoteSaving(true);
    try {
      const n = await addShipmentNote(awb, noteText);
      setNotes((prev) => [n, ...prev]);
      setNoteText("");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setNoteSaving(false); }
  };

  const toggleFlag = async () => {
    try {
      const r = await toggleCallFlag(awb);
      setCallFlag(!!r.to_call);
      toast.success(r.to_call ? "Marcat „De sunat”" : "Scos din „De sunat”");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const open = Boolean(awb);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose()}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto" data-testid="delivery-detail-drawer">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-primary" />
            AWB <span className="font-mono text-base">{awb}</span>
          </SheetTitle>
          <SheetDescription>Detalii și istoric tracking MyGLS</SheetDescription>
        </SheetHeader>

        {loading && (
          <div className="space-y-3 mt-6">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="h-12 bg-secondary rounded animate-pulse" />
            ))}
          </div>
        )}

        {!loading && data && (
          <div className="mt-5 space-y-5">
            {/* Status pill + sync */}
            <div className="flex items-start justify-between gap-3">
              <div>
                <Badge variant="outline" className={`gap-1.5 ${STATUS_COLORS[data.status] || ""}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[data.status] || ""}`} />
                  {STATUS_LABELS[data.status] || data.status}
                </Badge>
                <div className="text-xs text-muted-foreground mt-1.5">{data.status_description}</div>
                {data.last_status_at && (
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Ultim eveniment: {fmtDateTz(data.last_status_at)}
                  </div>
                )}
              </div>
              <Button onClick={onResync} disabled={syncing} variant="outline" size="sm" data-testid="delivery-detail-resync">
                <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
                Re-sync
              </Button>
            </div>

            {/* Quick info grid */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <InfoTile label="Comandă" value={data.order_name || "—"} />
              <InfoTile label="Transportator" value={(data.carrier || "gls").toUpperCase()} />
              <InfoTile label="Greutate" value={data.weight != null ? `${data.weight} kg` : "—"} />
              <InfoTile label="Timp tranzit" value={data.transit_days != null ? `${Number(data.transit_days).toFixed(1)} zile` : "—"} />
              <InfoTile label="Încercări livrare" value={data.delivery_attempts ?? 0} />
              <InfoTile label="COD" value={data.cod_amount != null ? `${Number(data.cod_amount).toFixed(2)} RON` : "—"} />
            </div>

            {/* Customer */}
            <div className="border border-border rounded-lg p-4 space-y-1.5">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Destinatar</div>
              <div className="font-semibold">{data.customer_name || "—"}</div>
              {data.customer_phone && (
                <div className="text-sm flex items-center gap-1.5 text-muted-foreground font-mono">
                  <Phone className="h-3.5 w-3.5" /> {data.customer_phone}
                </div>
              )}
              <div className="text-sm flex items-center gap-1.5 text-muted-foreground">
                <MapPin className="h-3.5 w-3.5" />
                {[data.city, data.county, data.zip_code].filter(Boolean).join(", ") || "—"}
              </div>
            </div>

            {/* Timeline */}
            <div>
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                Istoric tracking ({data.status_history?.length || 0} evenimente)
              </div>
              {(!data.status_history || data.status_history.length === 0) ? (
                <div className="text-sm text-muted-foreground py-6 text-center border border-dashed border-border rounded-lg">
                  Niciun eveniment înregistrat încă. Apasă Re-sync pentru a interoga MyGLS.
                </div>
              ) : (
                <ol className="relative border-l-2 border-border pl-5 space-y-3">
                  {[...data.status_history].reverse().map((h, i) => (
                    <li key={i} className="relative">
                      <span className={`absolute -left-[27px] top-1 h-3 w-3 rounded-full border-2 border-white ${STATUS_DOT[h.status] || "bg-zinc-400"}`} />
                      <div className="flex items-start justify-between gap-2">
                        <div className="text-sm font-medium">{h.description}</div>
                        <Badge variant="outline" className="text-[10px] py-0 h-5">{h.code}</Badge>
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-2 flex-wrap">
                        <Clock className="h-3 w-3" /> {h.at ? fmtDateTz(h.at) : ""}
                        {h.depot && <span>· {h.depot}</span>}
                        {h.info && <span className="text-muted-foreground/80">· {h.info}</span>}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
            </div>

            <div className="pt-3 border-t border-border space-y-4">
              {/* Quick actions (Partea 4) */}
              <div className="flex flex-wrap gap-2" data-testid="shipment-actions">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={toggleFlag}
                  className={callFlag ? "border border-amber-300 bg-amber-50 text-amber-800" : ""}
                  data-testid="shipment-toggle-call-flag"
                >
                  {callFlag ? <PhoneCall className="h-4 w-4 mr-1.5" /> : <PhoneOff className="h-4 w-4 mr-1.5" />}
                  {callFlag ? "Scos din De sunat" : "Marchează De sunat"}
                </Button>
                <a
                  href={`https://gls-group.eu/RO/ro/urmarire-colet?match=${awb}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md border border-border bg-secondary/40 hover:bg-secondary"
                  data-testid="delivery-detail-gls-link"
                >
                  <ExternalLink className="h-3.5 w-3.5" /> GLS
                </a>
              </div>

              {/* Notes (Partea 4) */}
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2 flex items-center gap-1.5">
                  <StickyNote className="h-3.5 w-3.5" /> Note interne ({notes.length})
                </div>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Adaugă notă (ex: client sună de la 14:00)"
                    className="h-9"
                    data-testid="shipment-note-input"
                  />
                  <Button
                    size="sm"
                    onClick={saveNote}
                    disabled={!noteText.trim() || noteSaving}
                    data-testid="shipment-note-save"
                  >
                    Adaugă
                  </Button>
                </div>
                {notes.length > 0 ? (
                  <div className="space-y-2 max-h-52 overflow-y-auto">
                    {notes.map((n, i) => (
                      <div key={i} className="rounded-md border border-border bg-muted/30 p-2 text-xs">
                        <div className="text-muted-foreground text-[10px] mb-0.5">
                          {n.by || "—"} · {fmtDateTz(n.created_at)}
                        </div>
                        <div>{n.text}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-muted-foreground italic">Nicio notă adăugată.</div>
                )}
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function InfoTile({ label, value }) {
  return (
    <div className="bg-secondary/40 rounded-md px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
      <div className="text-sm font-medium mt-0.5">{value}</div>
    </div>
  );
}
